import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Check,
  Copy,
  Info,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Share2,
} from 'lucide-react';

import API_BASE_URL from 'src/services/api.config';
import {
  SessionGenerationModal,
  type SessionGenerationFormData,
} from 'src/components/SessionGenerationModal';
import {
  createSonioSession,
  getSessionEnvironmentForFlow,
  openSessionStartUrl,
} from 'src/services/session-generation.service';
import { AbilityContext } from 'src/lib/ability';
import { useAppSelector } from 'src/hooks/useStore';
import { getFreshToken } from 'src/utils/get-fresh-token';
import { extractProfileFlowIds, getProfile } from 'src/services/users.service';
import {
  buildFlowNameLookup,
  isSonioDemoOrganization,
} from 'src/services/organizations.service';

/** Sonio Demo customer ID — used as a fallback when no organization is selected. */
const DEFAULT_CUSTOMER_IDS = '649025e334a855cf37cf58c7';

interface Flow {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'inactive' | 'draft';
  createdAt: string;
  updatedAt: string;
  nodesCount?: number;
  customerId?: string;
  customerName?: string;
  incodeClientId?: string;
  documentTypes?: string[];
  /** When false, create session against staging ID API instead of dev. */
  configExists?: boolean;
}

function parseConfigExists(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (value === 'false' || value === 0) return false;
  if (value === 'true' || value === 1) return true;
  return undefined;
}

/** Normalize API flow item to our Flow shape (flowName, customer, incodeClientId). */
function mapFlow(item: Record<string, unknown>, index: number): Flow {
  const flowName = String(item.flowName ?? item.name ?? '');
  const incodeClientId =
    item.incodeClientId != null
      ? String(item.incodeClientId)
      : item.externalIncodeId != null
        ? String(item.externalIncodeId)
        : undefined;
  const id =
    String(item.flowId ?? item.id ?? item._id ?? '').trim() ||
    `${flowName || 'flow'}-${incodeClientId ?? 'unknown'}-${index}`;
  const customerId =
    item.customerId != null
      ? String(item.customerId)
      : item.customer_id != null
        ? String(item.customer_id)
        : undefined;
  const configExists = parseConfigExists(item.configExists);
  return {
    id,
    name: flowName,
    description:
      item.description != null ? String(item.description) : undefined,
    status: (item.status as Flow['status']) ?? 'draft',
    createdAt: '',
    updatedAt: '',
    nodesCount: item.nodesCount != null ? Number(item.nodesCount) : undefined,
    customerId,
    customerName:
      item.customer != null
        ? String(item.customer)
        : String(item.customerName ?? ''),
    incodeClientId,
    documentTypes: Array.isArray(item.documentTypes)
      ? (item.documentTypes as string[])
      : undefined,
    configExists,
  };
}

const FlowManagementList = () => {
  const navigate = useNavigate();
  const ability = useContext(AbilityContext);
  const { getIdTokenClaims } = useAuth0();
  const { selectedOrganization, organizations } = useAppSelector(
    s => s.organization,
  );
  const { liveData } = useAppSelector(s => s.sliceTransaction);
  const [flows, setFlows] = useState<Flow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [sessionModalFlow, setSessionModalFlow] = useState<Flow | null>(null);
  const [useCustomCustomer, setUseCustomCustomer] = useState(false);
  const [customCustomerId, setCustomCustomerId] = useState('');
  const [listCustomerId, setListCustomerId] = useState(
    selectedOrganization?.customerId ?? DEFAULT_CUSTOMER_IDS,
  );

  const isSonioDemo = isSonioDemoOrganization(selectedOrganization);
  const flowNameLookup = useMemo(
    () => buildFlowNameLookup(organizations),
    [organizations],
  );

  const canCreateFlows = ability.can('create', 'Flows');
  const canCreateSessions = ability.can('create', 'Sessions');
  const canReadFlows =
    ability.can('read', 'Flows') ||
    ability.can('manage', 'Flows') ||
    canCreateFlows;
  const canViewFlowList = canReadFlows || canCreateSessions;
  const isAllOrganizationsSelected =
    !selectedOrganization || selectedOrganization.id === '';
  // Users without "Create new flow" must pick an org — not "All organizations".
  const needsOrgSelection = !canCreateFlows && isAllOrganizationsSelected;

  // Keep the queried customer ID in sync with the selected organization,
  // unless the user opted into a manual customer ID override.
  useEffect(() => {
    if (useCustomCustomer) return;
    if (needsOrgSelection) return;
    setListCustomerId(selectedOrganization?.customerId ?? DEFAULT_CUSTOMER_IDS);
  }, [selectedOrganization, useCustomCustomer, needsOrgSelection]);

  const fetchFlows = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!canViewFlowList) {
      setFlows([]);
      setLoading(false);
      return;
    }

    // Session operators / read-only flow users must select a specific organization.
    if (needsOrgSelection) {
      setFlows([]);
      setLoading(false);
      return;
    }

    const customerIdForFetch = useCustomCustomer
      ? listCustomerId
      : (selectedOrganization?.customerId ?? DEFAULT_CUSTOMER_IDS);

    try {
      const url = `${API_BASE_URL}/flows/list?customerIds=${encodeURIComponent(
        customerIdForFetch,
      )}`;
      const response = await axios.get(url, {
        headers: { 'Content-Type': 'application/json' },
      });
      const data = response?.data;
      const list = Array.isArray(data)
        ? data
        : (data?.flows ?? data?.data ?? []);
      let orgFlows = list.map((item: Record<string, unknown>, index: number) =>
        mapFlow(item, index),
      );

      // Scope the customer's flows down to the ones actually assigned to the
      // selected organization (flows already existing under that org).
      if (!useCustomCustomer && selectedOrganization?.flowIds?.length) {
        const orgFlowIdSet = new Set(
          selectedOrganization.flowIds.map(id => id.trim().toLowerCase()),
        );
        orgFlows = orgFlows.filter(flow =>
          orgFlowIdSet.has(flow.id.trim().toLowerCase()),
        );
      }

      const seenIds = new Set(orgFlows.map(flow => flow.id));
      const mergedFlows = [...orgFlows];

      // Sonio Demo also gets its flows from the newer per-user flow config
      // (profile.flowids), on top of the flows already existing above.
      if (!useCustomCustomer && isSonioDemo) {
        const token = await getFreshToken(getIdTokenClaims);
        if (token) {
          const profile = await getProfile(token, liveData);
          const profileFlowIds = extractProfileFlowIds(profile);
          for (const flowId of profileFlowIds) {
            if (seenIds.has(flowId)) continue;
            seenIds.add(flowId);
            mergedFlows.push({
              id: flowId,
              name: flowNameLookup[flowId] ?? flowId,
              status: 'active',
              createdAt: '',
              updatedAt: '',
              customerId: customerIdForFetch,
              customerName: selectedOrganization?.display_name ?? 'Sonio Demo',
            });
          }
        }
      }

      setFlows(mergedFlows);
    } catch (err: unknown) {
      const message =
        (
          err as {
            response?: { data?: { message?: string; error?: string } };
            message?: string;
          }
        )?.response?.data?.message ??
        (err as { response?: { data?: { message?: string; error?: string } } })
          ?.response?.data?.error ??
        (err as { message?: string })?.message ??
        'Failed to load flows';
      setError(String(message));
      setFlows([]);
    } finally {
      setLoading(false);
    }
  }, [
    listCustomerId,
    useCustomCustomer,
    selectedOrganization,
    isSonioDemo,
    flowNameLookup,
    getIdTokenClaims,
    liveData,
    canViewFlowList,
    needsOrgSelection,
  ]);

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows, selectedOrganization?.id]);

  const applyCustomerFilter = () => {
    if (!useCustomCustomer) {
      setListCustomerId(
        selectedOrganization?.customerId ?? DEFAULT_CUSTOMER_IDS,
      );
      return;
    }
    const nextId = customCustomerId.trim();
    if (!nextId) {
      setError('Enter a customer ID to load flows.');
      return;
    }
    setListCustomerId(nextId);
  };

  const handleEdit = (flowId: string) => {
    navigate(`/flows/new?flowId=${flowId}`);
  };

  const handleOpenCreateSession = (flow: Flow) => {
    if (!canCreateSessions) return;
    setSessionError(null);
    setSessionModalFlow(flow);
  };

  const handleGenerateSession = async (form: SessionGenerationFormData) => {
    if (!canCreateSessions || !sessionModalFlow) return;
    setSessionError(null);
    const customerId = sessionModalFlow.customerId ?? listCustomerId;
    try {
      const data = await createSonioSession({
        customerId,
        flowId: sessionModalFlow.id,
        form,
        theme: form.theme,
        language: form.language,
        environment: getSessionEnvironmentForFlow(sessionModalFlow),
      });
      if (!openSessionStartUrl(data)) {
        setSessionError('Session created but no start URL was returned.');
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        (err as { message?: string })?.message ??
        'Failed to create session';
      setSessionError(msg);
      throw err;
    }
  };

  // Non–flow-creators cannot override customer ID.
  useEffect(() => {
    if (canCreateFlows) return;
    setUseCustomCustomer(false);
    setCustomCustomerId('');
  }, [canCreateFlows]);

  const showSelectOrganizationHint = needsOrgSelection && canViewFlowList;

  const [copiedFlowId, setCopiedFlowId] = useState<string | null>(null);
  const copyFlowId = (id: string) => {
    navigator.clipboard?.writeText(id);
    setCopiedFlowId(id);
    setTimeout(() => setCopiedFlowId(null), 1500);
  };

  const headCell =
    'text-left text-[11px] font-bold uppercase tracking-[0.14em] text-[#5B6B80] px-4 py-3 bg-[#FAFBFD] border-b border-[#E5E7EB] whitespace-nowrap';

  return (
    <div className="min-h-screen bg-[#F7F9FC] px-4 sm:px-8 py-7">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-[-0.025em] text-[#0C2340]">
            Flow management
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            The verification journeys your customers go through.
          </p>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <button
            type="button"
            onClick={fetchFlows}
            disabled={loading}
            className="inline-flex items-center gap-2 h-[38px] px-4 rounded-md border-[1.5px] border-[#E5E7EB] bg-white text-sm font-semibold text-[#252525] hover:border-[#00387D] hover:text-[#00387D] transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {ability.can('create', 'Flows') && (
            <button
              type="button"
              onClick={() => navigate('/flows/new')}
              className="inline-flex items-center gap-2 h-[38px] px-4 rounded-md bg-[#00387D] hover:bg-[#002A5F] text-sm font-semibold text-white transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create new flow
            </button>
          )}
        </div>
      </div>

      {showSelectOrganizationHint && (
        <div className="flex items-start gap-2.5 px-3.5 py-3 mb-4 rounded-xl bg-[#EDF1F6] text-[13.5px] text-[#252525]">
          <Info className="h-[18px] w-[18px] text-[#00387D] flex-shrink-0 mt-px" />
          Select an organization from the sidebar to view its flows and create
          sessions.
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 px-3.5 py-3 mb-4 rounded-xl bg-[#B215151F] text-[13.5px] text-[#252525]">
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={fetchFlows}
            className="text-[13px] font-semibold text-[#00387D] hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {sessionError && (
        <div className="px-3.5 py-3 mb-4 rounded-xl bg-[#B215151F] text-[13.5px] text-[#252525]">
          {sessionError}
        </div>
      )}

      <section className="bg-white rounded-[14px] border-[1.5px] border-[#E5E7EB] overflow-hidden">
        {canCreateFlows && (
          <div className="flex flex-wrap items-center gap-3 px-4 py-3.5 border-b border-[#E5E7EB]">
            {useCustomCustomer ? (
              <input
                type="text"
                aria-label="Customer ID"
                placeholder="Enter customer ID"
                value={customCustomerId}
                onChange={e => setCustomCustomerId(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') applyCustomerFilter();
                }}
                className="h-[38px] w-full sm:w-[280px] px-3 rounded-md border-[1.5px] border-[#E5E7EB] font-mono text-[13px] text-[#252525] placeholder:text-[#5B6B80] focus:outline-none focus:border-[#00387D] focus:ring-[3px] focus:ring-[#00387D]/10"
              />
            ) : (
              <div className="flex items-center gap-2.5">
                <span className="text-[13px] font-semibold text-[#0C2340]">
                  Organization
                </span>
                <span className="inline-flex items-center h-[38px] px-3 rounded-md border-[1.5px] border-[#E5E7EB] bg-[#F7F9FC] text-sm text-[#252525] min-w-0 sm:min-w-[180px]">
                  {selectedOrganization?.display_name ?? 'All organizations'}
                </span>
              </div>
            )}
            <label className="inline-flex items-center gap-2 text-[13.5px] text-[#6B6B6B] cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[#00387D]"
                checked={useCustomCustomer}
                onChange={e => {
                  const checked = e.target.checked;
                  setUseCustomCustomer(checked);
                  setError(null);
                  if (!checked) {
                    setCustomCustomerId('');
                    setListCustomerId(
                      selectedOrganization?.customerId ?? DEFAULT_CUSTOMER_IDS,
                    );
                  }
                }}
              />
              Use a different customer ID
            </label>
            {useCustomCustomer && (
              <button
                type="button"
                onClick={applyCustomerFilter}
                disabled={loading || !customCustomerId.trim()}
                className="h-[38px] px-4 rounded-md border-[1.5px] border-[#E5E7EB] bg-white text-sm font-semibold text-[#252525] hover:border-[#00387D] hover:text-[#00387D] transition-colors disabled:opacity-50"
              >
                Load flows
              </button>
            )}
            {!loading && (
              <span className="ml-auto text-[13px] text-[#5B6B80]">
                {flows.length} flow{flows.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-14">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#E5E7EB] border-t-[#00387D]" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm md:min-w-[720px]">
              <thead>
                <tr>
                  <th className={headCell}>Flow name</th>
                  <th className={`${headCell} max-md:hidden`}>Documents</th>
                  <th className={`${headCell} max-md:hidden`}>Customer</th>
                  <th className={`${headCell} max-md:hidden`}>SONIO flow ID</th>
                  <th className={`${headCell} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {flows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-14 text-center">
                      <Share2
                        className="h-7 w-7 text-[#B6C2D2] mx-auto mb-2"
                        strokeWidth={1.75}
                      />
                      <p className="text-sm text-[#5B6B80]">
                        {showSelectOrganizationHint
                          ? 'Select an organization from the sidebar to view its flows.'
                          : 'No flows found.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  flows.map(flow => (
                    <tr
                      key={flow.id}
                      className="group border-b border-[#EEF1F5] last:border-b-0 hover:bg-[#F8FAFD] transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Share2
                            className="h-[22px] w-[22px] text-[#5B6B80] group-hover:text-[#00387D] transition-colors flex-shrink-0"
                            strokeWidth={1.75}
                          />
                          <div className="min-w-0">
                            <p className="font-semibold text-[#0C2340] truncate">
                              {flow.name || '—'}
                            </p>
                            {flow.description && (
                              <p className="text-[12.5px] text-[#5B6B80] truncate">
                                {flow.description}
                              </p>
                            )}
                            <div className="md:hidden mt-1.5 flex flex-col items-start gap-1.5">
                              {flow.documentTypes &&
                                flow.documentTypes.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {flow.documentTypes.map(doc => (
                                      <span
                                        key={doc}
                                        className="inline-flex items-center rounded-md bg-[#F7F9FC] px-2 py-[3px] text-xs font-semibold text-[#6B6B6B]"
                                      >
                                        {doc}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              {flow.id && (
                                <button
                                  type="button"
                                  onClick={() => copyFlowId(flow.id)}
                                  className="inline-flex items-center gap-1.5 font-mono text-[11.5px] text-[#00387D] break-all text-left"
                                >
                                  {flow.id}
                                  {copiedFlowId === flow.id ? (
                                    <Check className="h-3.5 w-3.5 flex-shrink-0 text-[#2E8A4A]" />
                                  ) : (
                                    <Copy className="h-3 w-3 flex-shrink-0" />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 max-md:hidden">
                        {flow.documentTypes && flow.documentTypes.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {flow.documentTypes.map(doc => (
                              <span
                                key={doc}
                                className="inline-flex items-center rounded-md bg-[#F7F9FC] px-2 py-[3px] text-xs font-semibold text-[#6B6B6B]"
                              >
                                {doc}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[#5B6B80]">–</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#252525] max-md:hidden">
                        {flow.customerName || '—'}
                      </td>
                      <td className="px-4 py-3 max-md:hidden">
                        {flow.id ? (
                          <button
                            type="button"
                            onClick={() => copyFlowId(flow.id)}
                            title="Copy flow ID"
                            className="inline-flex items-center gap-1.5 font-mono text-[12.5px] text-[#6B6B6B] hover:text-[#00387D]"
                          >
                            {flow.id}
                            {copiedFlowId === flow.id ? (
                              <Check className="h-3.5 w-3.5 text-[#2E8A4A]" />
                            ) : (
                              <Copy className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                            )}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5 justify-end items-center max-md:flex-col-reverse max-md:items-end">
                          {canCreateSessions && (
                            <button
                              type="button"
                              onClick={() => handleOpenCreateSession(flow)}
                              aria-label="Create session"
                              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border-[1.5px] border-[#E5E7EB] bg-white text-[13px] font-semibold text-[#252525] hover:border-[#00387D] hover:text-[#00387D] transition-colors whitespace-nowrap"
                            >
                              <Play className="h-3.5 w-3.5" />
                              <span className="max-sm:hidden">
                                Create session
                              </span>
                            </button>
                          )}
                          {ability.can('create', 'Flows') &&
                            flow.configExists !== false && (
                              <button
                                type="button"
                                onClick={() => handleEdit(flow.id)}
                                aria-label="Edit flow"
                                title="Edit configuration"
                                className="h-[34px] w-[34px] rounded-[9px] grid place-items-center text-[#5B6B80] hover:bg-[#F7F9FC] hover:text-[#00387D] transition-colors"
                              >
                                <Pencil
                                  className="h-[17px] w-[17px]"
                                  strokeWidth={1.75}
                                />
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <SessionGenerationModal
        open={sessionModalFlow != null}
        onOpenChange={open => {
          if (!open) setSessionModalFlow(null);
        }}
        flowName={sessionModalFlow?.name}
        onGenerate={handleGenerateSession}
      />
    </div>
  );
};

export default FlowManagementList;
